import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getStartedManager } from "@core/service-container";
import styled from "@emotion/styled";
import { row, stack } from "@ui/style/layout";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { BaseExercise, ExerciseBodyPart, ExerciseCategory } from "@domain/exercise/exercise";
import { Checkbox } from "primereact/checkbox";
import { BodyPartIcon } from "@ui/components/exercise/body-part-icon";
import { colors } from "@ui/style/colors";
import { useExerciseFormatter } from "@ui/components/exercise/hooks/use-exercise-formatter";
import { ConditionalSkeleton } from "@ui/components/common/conditional-skeleton";
import { isNil, isNotNil } from "@utils/assert";
import { exerciseFilterManager } from "@core/service-container";
import { useExercisePage } from "@ui/pages/exercise/hooks/use-exercise-page";
import { createSprite } from "@utils/canvas-utils";
import { physioMeMobileBreakpoint } from "@ui/style";

// 'exercise' is only present when not loading; make it optional with never in the loading case
// to avoid redundant `undefined` annotation while still allowing the prop to be omitted.
type ExerciseCardProps =
	| { loading: true; exercise?: never }
	| { loading?: false; exercise: BaseExercise };

export const ExerciseCard: React.FC<ExerciseCardProps> = ({ loading = false, exercise }) => {
	const { formatExerciseCategory } = useExerciseFormatter();
	const { selectedExercises, openFilteredExerciseDetailsModal } = useExercisePage();
	const { isMobile } = useDimensions();
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const [sprite, setSprite] = useState<string | null>(null);
	const exer = useMemo(() => (isNotNil(exercise) ? exercise : DUMMY_EXERCISE), [exercise]);
	const isAnimationAvailable = useMemo(() => !isMobile && exer.keyframeUrls.length > 1, [isMobile, exer.keyframeUrls]);
	const ids = useMemo(
		() => ({
			animationId: `exercise-card-anim-${exer.id}`,
			animationBackgroundId: `exercise-card-anim-bg-${exer.id}`,
		}),
		[exer.id],
	);
	const isChecked = useMemo(
		() => isNotNil(exercise) && selectedExercises.includes(exercise),
		[exercise, selectedExercises],
	);

	useEffect(() => {
		(async () => {
			if (isAnimationAvailable) {
				const builtSprite = await createSprite(canvasRef.current, exer.keyframeUrls);
				if (isNotNil(builtSprite)) setSprite(builtSprite);
			}
		})();
	}, [isAnimationAvailable, exer.keyframeUrls]);

	const handleCheckboxClick = useCallback(
		(selected: boolean) => {
			if (isNotNil(exercise)) exerciseFilterManager.updateSelectedExercise(exercise, selected);
		},
		[exercise],
	);

	const handleExerciseClick = useCallback(async () => {
		if (loading) return;
		if (isNotNil(exercise)) openFilteredExerciseDetailsModal(exercise);
		await getStartedManager.tryMarkExercisesStepAsCompleted();
	}, [exercise, loading, openFilteredExerciseDetailsModal]);

	return (
		<ExerciseContainer loading={loading} onClick={async () => handleExerciseClick()}>
			<ConditionalSkeleton active={loading} loading={loading}>
				<StyledCheckbox
					checked={isChecked}
					onClick={(event: React.MouseEvent) => {
						event.stopPropagation();
						handleCheckboxClick(!isChecked);
					}}
				/>
				<ImgContainer
					isAnimationEnabled={isAnimationAvailable}
					animationId={ids.animationId}
					animationBackgroundId={ids.animationBackgroundId}
					stepCount={exer.keyframeUrls.length}
				>
					<Thumbnail src={exer.thumbnails.mediumThumbnailUrl} isLoading={isNil(exercise)} />
					{isAnimationAvailable && (
						<>
							<StyledCanvas ref={canvasRef} />
							{sprite && (
								<>
									<AnimationBackground className={ids.animationBackgroundId} />
									<Animation className={ids.animationId} sprite={sprite} />
								</>
							)}
						</>
					)}
				</ImgContainer>
			</ConditionalSkeleton>
			<Details>
				<Content>
					<ConditionalSkeleton contentDriven={true} active={loading} loading={loading}>
						<Category className="p-size-s-semi-bold">{formatExerciseCategory(exer.category)}</Category>
					</ConditionalSkeleton>
					<ConditionalSkeleton contentDriven={true} active={loading} loading={loading}>
						<Title className="p-size-s-rubik-medium">{exer.title}</Title>
					</ConditionalSkeleton>
				</Content>
				<BodyPartIconContainer>
					<ConditionalSkeleton contentDriven={true} active={loading} loading={loading}>
						<BodyPartIcon
							bodyPart={exer.primaryBodyPart}
							additionalBodyPartsCount={exer.secondaryBodyParts.length}
							width={24}
							height={24}
						/>
					</ConditionalSkeleton>
				</BodyPartIconContainer>
			</Details>
		</ExerciseContainer>
	);
};

const THUMBNAIL_IMAGE_HEIGHT = 160;
const ANIMATION_IMAGE_HEIGHT = THUMBNAIL_IMAGE_HEIGHT;
const ANIMATION_IMAGE_WIDTH = ANIMATION_IMAGE_HEIGHT;
const ANIMATION_DELAY_MS = 500;
const ANIMATION_FRAME_DURATION_MS = 650;

const DUMMY_EXERCISE = {
	id: "dummy",
	title: "Title dummy exercise",
	category: ExerciseCategory.WarmUp,
	primaryBodyPart: ExerciseBodyPart.UpperLimbs,
	secondaryBodyParts: [],
	thumbnails: { mediumThumbnailUrl: undefined },
	keyframeUrls: [],
};

const ExerciseContainer = styled.div<{ loading: boolean }>`
	${stack(4, "flexstart", "stretch")};
	cursor: ${({ loading }) => (loading ? "default" : "pointer")};
	position: relative;
	width: 286px;

	@media ${physioMeMobileBreakpoint} {
		width: 100%;
	}
`;

const StyledCheckbox = styled(Checkbox)`
	position: absolute;
	top: 0;
	left: 0;
	padding: 12px 20px 20px 12px;
	z-index: 1;
`;

const ImgContainer = styled.div<{
	isAnimationEnabled: boolean;
	animationId: string;
	animationBackgroundId: string;
	stepCount: number;
}>`
	${row(undefined, "center", "center")};

	${({ isAnimationEnabled, animationId, animationBackgroundId, stepCount }) =>
		isAnimationEnabled &&
		`
		&:hover .${animationId} {
			animation: anim-${animationId} ${stepCount * ANIMATION_FRAME_DURATION_MS}ms steps(${stepCount}) ${ANIMATION_DELAY_MS}ms infinite;

			@keyframes anim-${animationId} {
				0% {
					visibility: visible;
					background-position: 0 0;
				}
				100% {
					background-position: -${stepCount * ANIMATION_IMAGE_WIDTH}px 0;
				}
			}
    }
    
    &:hover .${animationBackgroundId} {
      animation: anim-${animationBackgroundId} 1ms ${ANIMATION_DELAY_MS}ms forwards;
    }

    @keyframes anim-${animationBackgroundId} {
      100% {
        visibility: visible;
      }
    }
	`}
`;

const StyledCanvas = styled.canvas`
	display: none;
`;

const Thumbnail = styled.img<{ isLoading: boolean }>`
	width: 100%;
	height: ${THUMBNAIL_IMAGE_HEIGHT}px;
	object-fit: cover;
	border-radius: 8px;

	@media ${physioMeMobileBreakpoint} {
		${({ isLoading }) =>
			!isLoading &&
			`
			height: ${THUMBNAIL_IMAGE_HEIGHT}px;
		`}
	}
`;

const Animation = styled.div<{ sprite: string }>`
	visibility: hidden;
	position: absolute;
	width: ${ANIMATION_IMAGE_WIDTH}px;
	height: ${ANIMATION_IMAGE_HEIGHT}px;
	background-image: url("${({ sprite }) => sprite}");
	background-size: auto 100%;
	background-repeat: no-repeat;
`;

const AnimationBackground = styled.div`
	visibility: hidden;
	position: absolute;
	width: 100%;
	height: ${ANIMATION_IMAGE_HEIGHT}px;
	background-color: ${colors.primary10};
	border-radius: 8px;
	z-index: 0;
`;

const Details = styled.div`
	${row(4, "spacebetween", "stretch")};
`;

const Category = styled.p`
	color: ${colors.grey80};
`;

const Title = styled.div`
	flex: 1;
`;

const Content = styled.div`
	${stack(4, "flexstart", "stretch")};
`;

const BodyPartIconContainer = styled.div`
	${row(undefined, "flexstart", "stretch")};
	padding: 7px 6px 0 0;
`;
